import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Banknote, Headphones, Truck, Undo2 } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { useSeo } from "@/hooks/useSeo";
import { media } from "@/lib/api";
import { audiencePath, brandPath, collectionPath, deptPath } from "@/lib/nav";
import { useI18n } from "@/context/Shop";
import { ProductRail, SkeletonGrid } from "@/components/store/ProductCard";

const fade = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } };
const HERO = "https://images.unsplash.com/photo-1587115924362-622c3fa065bd";

export default function Home() {
  const { data, loading } = useFetch("/store/home");
  const { t, tr } = useI18n();
  useSeo(null, t("hero_sub"));
  const trust = [
    [Truck, t("trust_delivery"), t("trust_delivery_sub")],
    [Banknote, t("trust_payment"), t("trust_payment_sub")],
    [Undo2, t("trust_returns"), t("trust_returns_sub")],
    [Headphones, t("trust_care"), t("trust_care_sub")],
  ];
  return (
    <div data-testid="home-page">
      {/* campaign hero: black primary view */}
      <section className="bg-[hsl(var(--header-bg))] text-[hsl(var(--header-fg))]" data-testid="home-hero">
        <div className="container-lux grid lg:grid-cols-12 items-stretch">
          <div className="lg:col-span-5 flex flex-col justify-center py-14 lg:py-24 lg:pr-12 order-2 lg:order-1">
            <motion.p {...fade} className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--header-muted))]">{t("hero_eyebrow")}</motion.p>
            <motion.h1 {...fade} transition={{ ...fade.transition, delay: 0.08 }} className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] font-medium tracking-[-0.01em] mt-5">{t("hero_title")}</motion.h1>
            <motion.p {...fade} transition={{ ...fade.transition, delay: 0.16 }} className="text-base text-white/75 mt-6 max-w-md leading-relaxed">{t("hero_sub")}</motion.p>
            <motion.div {...fade} transition={{ ...fade.transition, delay: 0.24 }} className="flex flex-wrap gap-3 mt-10">
              <Link to="/new-arrivals" className="btn-paper" data-testid="home-hero-cta">{t("hero_cta")}</Link>
              <Link to="/brands" className="btn-paper-outline" data-testid="home-hero-cta-secondary">{t("hero_cta2")}</Link>
            </motion.div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-12" data-testid="home-hero-audiences">
              {(data?.audiences || [{ key: "women" }, { key: "men" }, { key: "kids" }]).map((a) => (
                <Link key={a.key} to={audiencePath(a.key)} className="nav-link text-white/80 hover:text-white underline-grow" data-testid={`home-audience-${a.key}-link`}>{t(a.key)}</Link>
              ))}
            </div>
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9 }} className="lg:col-span-7 relative overflow-hidden aspect-[4/5] sm:aspect-[16/10] lg:aspect-auto lg:min-h-[640px] order-1 lg:order-2 grain">
            <img src={media(HERO, 1800)} alt="Editorial campaign" className="absolute inset-0 w-full h-full object-cover" fetchpriority="high" />
          </motion.div>
        </div>
      </section>

      {loading ? <div className="container-lux py-16"><SkeletonGrid n={4} /></div> : <ProductRail title={t("new_arrivals")} items={data?.new_arrivals} link="/new-arrivals" testid="home-new-arrivals" />}

      {/* audiences */}
      <section className="container-lux" data-testid="home-audience-tiles">
        <p className="eyebrow mb-5">{t("shop_for")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
          {(data?.audiences || []).map((a) => (
            <motion.div {...fade} key={a.key}>
              <Link to={audiencePath(a.key)} className="group block relative overflow-hidden aspect-[4/5] bg-muted" data-testid={`home-tile-${a.key}`}>
                {a.image_url && <img src={media(a.image_url, 900)} alt={t(a.key)} className="absolute inset-0 w-full h-full object-cover img-zoom" loading="lazy" />}
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-7 left-7 text-white">
                  <p className="font-display text-4xl">{t(a.key)}</p>
                  <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] mt-3 border-b border-white/70 pb-0.5">{t("shop_now")} <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* departments */}
      {data?.departments?.length > 0 && (
        <section className="container-lux pt-14 lg:pt-20" data-testid="home-departments">
          <p className="eyebrow mb-5">{t("departments")}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {data.departments.map((d) => (
              <motion.div {...fade} key={d.slug}>
                <Link to={deptPath(d.slug)} className="group block" data-testid={`home-department-${d.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-muted">{d.image_url && <img src={media(d.image_url, 900)} alt={tr(d)} className="w-full h-full object-cover img-zoom" loading="lazy" />}</div>
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-[12px] uppercase tracking-[0.16em] font-semibold">{tr(d)}</p>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* brands */}
      {data?.brands?.length > 0 && (
        <section className="border-y mt-16 lg:mt-24" data-testid="home-brands">
          <div className="container-lux py-14">
            <p className="eyebrow text-center">{t("our_brands")}</p>
            <div className="flex flex-wrap justify-center gap-x-10 gap-y-4 mt-7">
              {data.brands.map((b) => <Link key={b.slug} to={brandPath(b.slug)} className="font-display text-2xl sm:text-3xl text-foreground/75 hover:text-foreground transition-colors" data-testid={`home-brand-${b.slug}`}>{b.name}</Link>)}
            </div>
            <div className="text-center mt-8"><Link to="/brands" className="text-[12px] uppercase tracking-[0.14em] font-semibold link-lux">{t("all_brands")}</Link></div>
          </div>
        </section>
      )}

      {data?.picks?.length > 0 && <ProductRail title={tr({ name: data.picks_title, name_sq: data.picks_title_sq })} items={data.picks} link="/collections/our-picks" testid="home-picks" eyebrow={t("edit")} />}

      {data?.collections?.length > 0 && (
        <section className="container-lux py-12 lg:py-16" data-testid="home-collections">
          <div className="flex items-end justify-between mb-8"><h2 className="font-display text-3xl sm:text-4xl font-medium">{t("curated")}</h2><Link to="/collections" className="text-[12px] uppercase tracking-[0.14em] font-semibold link-lux">{t("view_all")}</Link></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {data.collections.slice(0, 4).map((c) => (
              <motion.div {...fade} key={c.slug}>
                <Link to={collectionPath(c.slug)} className="group block" data-testid={`home-collection-${c.slug}`}>
                  <div className="aspect-[4/5] overflow-hidden bg-muted">{c.hero_image_url && <img src={media(c.hero_image_url, 900)} alt="" className="w-full h-full object-cover img-zoom" loading="lazy" />}</div>
                  <p className="font-display text-xl mt-4 leading-tight">{tr(c)}</p>
                  <p className="text-[13px] text-muted-foreground mt-1 line-clamp-2">{tr(c, "description")}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      <ProductRail title={t("sale_edit")} items={data?.sale} link="/sale" testid="home-sale" linkLabel={t("shop_sale")} />

      <section className="border-t" data-testid="home-trust">
        <div className="container-lux py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {trust.map(([Icon, title, sub]) => (
            <div key={title} className="flex items-start gap-3">
              <Icon className="h-5 w-5 mt-0.5 shrink-0" strokeWidth={1.4} />
              <div><p className="text-[12px] uppercase tracking-[0.12em] font-semibold">{title}</p><p className="text-[13px] text-muted-foreground mt-1 leading-relaxed">{sub}</p></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
